import { useEffect, useState } from 'react';
import { useParams } from 'wouter';
import { useNav } from '@/lib/nav';
import routes from '@/lib/routes';
import Header from '@/components/layout/header';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/auth/AuthProvider';
import { useProjectsStore } from '@/store/projects';
import { useFigureCompositionStore } from '@/store/figure-composition';
import { Plus, Loader2, FileImage, Edit, Trash2, LayoutGrid } from 'lucide-react';
import { formatRelativeTime } from '@/components/gallery/plot-card';
import { authorizedFetch } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { ChevronRight } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export default function ProjectFiguresPage() {
  const { projectId } = useParams();
  const { go } = useNav();
  const { user } = useAuth();
  const { currentProject, setCurrentProject } = useProjectsStore();
  const { compositions, isLoading, error, listCompositionsForProject, deleteComposition } = useFigureCompositionStore();
  const { toast } = useToast();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [figureToDelete, setFigureToDelete] = useState<{ id: string; title: string } | null>(null);

  useEffect(() => {
    if (!user) {
      go(routes.home());
      return;
    }
    if (!projectId) return;

    // Load project
    authorizedFetch(`/api/projects/${projectId}`)
      .then((res) => res.json())
      .then((project) => setCurrentProject(project))
      .catch(() => go(routes.dashboard()));

    // Load figures
    listCompositionsForProject(projectId).catch((err) => {
      toast({
        title: 'Failed to load multi-panel figures',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    });
  }, [projectId, user, setCurrentProject, listCompositionsForProject, go, toast]);

  const handleDeleteClick = (id: string, title: string) => {
    setFigureToDelete({ id, title });
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!figureToDelete) return;

    try {
      await deleteComposition(figureToDelete.id);
      toast({
        title: 'Multi-panel figure deleted',
        description: `"${figureToDelete.title}" has been deleted.`,
      });
      setDeleteDialogOpen(false);
      setFigureToDelete(null);
    } catch (err) {
      toast({
        title: 'Failed to delete multi-panel figure',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="container mx-auto px-6 py-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
          <Button variant="ghost" size="sm" onClick={() => go(routes.dashboard())}>
            Projects
          </Button>
          <ChevronRight className="h-4 w-4" />
          <Button variant="ghost" size="sm" onClick={() => go(routes.project.root(projectId!))}>
            {currentProject?.name || 'Project'}
          </Button>
          <ChevronRight className="h-4 w-4" />
          <span className="text-foreground">Multi-panel Figures</span>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold">Multi-panel Figures</h1>
            <p className="text-muted-foreground mt-1">
              Create and manage multi-panel figures
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => go(routes.project.gallery(projectId!))}
              disabled={isLoading}
            >
              <LayoutGrid className="mr-2 h-4 w-4" />
              Gallery
            </Button>
            <Button
              onClick={() => go(routes.project.figures.new(projectId!))}
              disabled={isLoading}
            >
              <Plus className="mr-2 h-4 w-4" />
              New Figure
            </Button>
          </div>
        </div>

        {/* Error display */}
        {error && (
          <Card className="p-4 mb-4 border-destructive bg-destructive/10">
            <p className="text-sm text-destructive">{error}</p>
          </Card>
        )}

        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">Loading multi-panel figures...</span>
          </div>
        )}

        {/* Figures list */}
        {!isLoading && compositions.length === 0 && (
          <Card className="p-12 text-center">
            <FileImage className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No multi-panel figures yet</h3>
            <p className="text-muted-foreground mb-4">
              Create your first multi-panel figure to get started.
            </p>
            <Button onClick={() => go(routes.project.figures.new(projectId!))}>
              <Plus className="mr-2 h-4 w-4" />
              Create Figure
            </Button>
          </Card>
        )}

        {!isLoading && compositions.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {compositions.map((comp) => (
              <Card key={comp.id} className="p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-semibold text-lg truncate flex-1">
                    {comp.title || 'Untitled Figure'}
                  </h3>
                </div>
                {comp.caption && (
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                    {comp.caption}
                  </p>
                )}
                <div className="text-xs text-muted-foreground mb-4">
                  <div>Panels: {comp.panels.length}</div>
                  <div>
                    Updated {formatRelativeTime(comp.updatedAt)}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => go(routes.project.figures.edit(projectId!, comp.id))}
                  >
                    <Edit className="mr-2 h-4 w-4" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteClick(comp.id, comp.title || 'Untitled Figure')}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Multi-panel Figure</AlertDialogTitle>
              <AlertDialogDescription>
                Delete multi-panel figure "{figureToDelete?.title || 'Untitled Figure'}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setFigureToDelete(null)}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteConfirm}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

